import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Sparkles, EyeOff, Play, Coffee, Wrench, Clock, Video, Edit3, Timer,
  Image as ImageIcon, ExternalLink, Maximize2, LogOut, Save, Plus, Trash2,
  Upload, FileImage, Check, X, Users, Lock, Library,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUserData } from '../contexts/UserDataContext';
import TemplateLibrary from './TemplateLibrary';
import MusicPlayer from './MusicPlayer';
import TimezoneSelect from './TimezoneSelect';
import { Input } from './ui/input';
import TimePicker12h from './TimePicker12h';
import './ControlPanel.css';

const templateIcons = {
  'session-starting': Play,
  'break-progress': Coffee,
  'technical-intermission': Wrench,
  'next-panel': Clock,
  'stand-by': Clock,
  'recording-progress': Video,
  'custom': Edit3,
};

export const ControlPanel = ({
  allTemplates,
  currentTemplate,
  onTemplateChange,
  title,
  onTitleChange,
  paragraph,
  onParagraphChange,
  lowerText,
  onLowerTextChange,
  animationEnabled,
  onAnimationToggle,
  onTogglePanel,
  timerEnabled,
  onTimerToggle,
  targetTime,
  onTargetTimeChange,
  timerLabel,
  onTimerLabelChange,
  targetTimezone,
  onTargetTimezoneChange,
  manualMode,
  onManualModeToggle,
  manualCurrentTime,
  onManualCurrentTimeChange,
  autoCloseOnTimerEnd,
  onAutoCloseToggle,
  backgroundKey,
  onBackgroundChange,
  onOpenPresenter,
  onEnterFullscreen,
}) => {
  const { user, logout } = useAuth();
  const {
    allBackgrounds,
    findTemplate,
    findBackground,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    uploadBackground,
    updateBackground,
    deleteBackground,
  } = useUserData();

  const activeTpl = findTemplate(currentTemplate);
  const isBuiltinTpl = !activeTpl || activeTpl.builtin;
  // For custom templates: only the owner can save/delete. Built-ins are read-only for everyone.
  const canEditTpl = activeTpl && !activeTpl.builtin && activeTpl.is_mine !== false;
  const activeBg = findBackground(backgroundKey);
  const canEditBg = activeBg && !activeBg.builtin && activeBg.is_mine !== false;

  // ===== Template actions =====
  const [savingTpl, setSavingTpl] = useState(false);
  const [showSaveAsNew, setShowSaveAsNew] = useState(false);
  const [newTplName, setNewTplName] = useState('');
  const [tplFlash, setTplFlash] = useState('');
  const [libraryOpen, setLibraryOpen] = useState(false);

  const flashMsg = (msg) => {
    setTplFlash(msg);
    setTimeout(() => setTplFlash(''), 2200);
  };

  const handleSaveTpl = async () => {
    if (!canEditTpl) return;
    setSavingTpl(true);
    try {
      await updateTemplate(activeTpl.id, {
        title, paragraph, lower_text: lowerText, name: activeTpl.name,
      });
      flashMsg('Saved');
    } catch (e) {
      flashMsg('Save failed');
    } finally {
      setSavingTpl(false);
    }
  };

  const handleSaveAsNew = async () => {
    const name = newTplName.trim();
    if (!name) return;
    setSavingTpl(true);
    try {
      const created = await createTemplate({
        name, title, paragraph, lower_text: lowerText, visibility: 'private',
      });
      onTemplateChange(created.id);
      setShowSaveAsNew(false);
      setNewTplName('');
      flashMsg(`Created "${name}"`);
    } catch (e) {
      flashMsg('Create failed');
    } finally {
      setSavingTpl(false);
    }
  };

  const handleDeleteTpl = async () => {
    if (!canEditTpl) return;
    const ok = window.confirm(`Delete template "${activeTpl.name}"?`);
    if (!ok) return;
    try {
      await deleteTemplate(activeTpl.id);
      onTemplateChange('session-starting');
      flashMsg('Deleted');
    } catch (e) {
      flashMsg('Delete failed');
    }
  };

  const handleToggleTplVisibility = async () => {
    if (!canEditTpl) return;
    const next = activeTpl.visibility === 'team' ? 'private' : 'team';
    setSavingTpl(true);
    try {
      await updateTemplate(activeTpl.id, { visibility: next });
      flashMsg(next === 'team' ? 'Shared with team' : 'Made private');
    } catch (e) {
      flashMsg('Update failed');
    } finally {
      setSavingTpl(false);
    }
  };

  // ===== Background upload =====
  const fileInputRef = useRef(null);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [bgError, setBgError] = useState('');
  const [bgFlash, setBgFlash] = useState('');
  const [bgDragOver, setBgDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  const flashBg = (msg) => {
    setBgFlash(msg);
    setTimeout(() => setBgFlash(''), 2200);
  };

  // Shared upload pipeline used by the file input AND the drop zone.
  // Validates types/sizes and uploads serially so we don't blow the backend's
  // 4 MB-per-image limit. Only the *last* uploaded background becomes active.
  const uploadBgFiles = async (fileList) => {
    const ALLOWED = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    const files = Array.from(fileList || []).filter((f) => ALLOWED.includes(f.type));
    if (!files.length) {
      setBgError('Only PNG, JPG, or WebP images are accepted');
      return;
    }
    const tooBig = files.find((f) => f.size > 4 * 1024 * 1024);
    if (tooBig) {
      setBgError(`"${tooBig.name}" exceeds 4 MB`);
      return;
    }
    setBgError('');
    setUploadingBg(true);
    let lastCreated = null;
    let succeeded = 0;
    try {
      for (const file of files) {
        const name = file.name.replace(/\.[^.]+$/, '');
        try {
          lastCreated = await uploadBackground(file, name, 'private');
          succeeded += 1;
        } catch (e) {
          setBgError(`Failed to upload "${file.name}"`);
        }
      }
      if (lastCreated) onBackgroundChange(lastCreated.id);
      if (succeeded > 0) {
        flashBg(succeeded === 1 ? 'Uploaded' : `Uploaded ${succeeded} images`);
      }
    } finally {
      setUploadingBg(false);
    }
  };

  const handleBgFileChosen = async (e) => {
    const files = e.target.files;
    e.target.value = '';
    if (!files?.length) return;
    await uploadBgFiles(files);
  };

  const handleBgDragEnter = (e) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    setBgDragOver(true);
  };

  const handleBgDragOver = (e) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleBgDragLeave = (e) => {
    e.preventDefault();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setBgDragOver(false);
  };

  const handleBgDrop = async (e) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    dragCounterRef.current = 0;
    setBgDragOver(false);
    const files = e.dataTransfer.files;
    if (files?.length) await uploadBgFiles(files);
  };

  const handleDeleteBg = async () => {
    if (!canEditBg) return;
    const ok = window.confirm(`Delete background "${activeBg.name}"?`);
    if (!ok) return;
    try {
      await deleteBackground(activeBg.id);
      onBackgroundChange('branded-light');
    } catch (e) {
      setBgError('Delete failed');
    }
  };

  const handleToggleBgVisibility = async () => {
    if (!canEditBg) return;
    const next = activeBg.visibility === 'team' ? 'private' : 'team';
    try {
      await updateBackground(activeBg.id, { visibility: next });
      flashBg(next === 'team' ? 'Shared with team' : 'Made private');
    } catch (e) {
      setBgError('Update failed');
    }
  };

  return (
    <div className="control-panel" data-testid="control-panel">
      <div className="panel-header">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-teal-400" />
          <h2 className="text-lg font-semibold text-white">Control Panel</h2>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onTogglePanel}
          className="text-gray-400 hover:text-white hover:bg-gray-800"
          title="Hide Control Panel"
        >
          <EyeOff className="w-4 h-4" />
        </Button>
      </div>

      {user && (
        <div className="panel-user" data-testid="user-menu">
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="user-avatar" referrerPolicy="no-referrer" />
          ) : (
            <div className="user-avatar user-avatar-fallback">
              {(user.name || user.email || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="user-meta">
            <div className="user-name" title={user.name}>{user.name}</div>
            <div className="user-email" title={user.email}>{user.email}</div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="text-gray-400 hover:text-white hover:bg-gray-800 h-8 px-2"
            title="Sign out"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      )}

      <div className="panel-content">
        {/* Live Share / Presenter */}
        <Card className="p-4 bg-teal-950/30 border-teal-900/50" data-testid="share-card">
          <div className="flex items-center gap-2 mb-2">
            <Video className="w-4 h-4 text-teal-400" />
            <Label className="text-sm font-medium text-teal-100">Live Share Mode</Label>
          </div>
          <p className="text-xs text-teal-200/70 mb-3 leading-relaxed">
            Open a clean presenter window to share in Zoom / Teams. All edits sync live.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              onClick={onOpenPresenter}
              className="bg-teal-500 hover:bg-teal-400 text-gray-900 font-medium h-9"
              data-testid="open-presenter-btn"
            >
              <ExternalLink className="w-4 h-4 mr-1.5" />
              Presenter
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onEnterFullscreen}
              className="border-teal-700 bg-transparent text-teal-200 hover:bg-teal-900/40 hover:text-teal-100 h-9"
              data-testid="fullscreen-btn"
            >
              <Maximize2 className="w-4 h-4 mr-1.5" />
              Fullscreen
            </Button>
          </div>
        </Card>

        {/* Template Selector */}
        <Card className="p-4 bg-gray-900/50 border-gray-800" data-testid="template-card">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-medium text-gray-300">Template</Label>
            <div className="flex items-center gap-2">
              {tplFlash && (
                <span className="text-xs text-teal-300 flex items-center gap-1">
                  <Check className="w-3 h-3" /> {tplFlash}
                </span>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setLibraryOpen(true)}
                className="h-7 px-2 text-xs text-teal-300 hover:bg-teal-950/40 hover:text-teal-200"
                data-testid="open-library-btn"
                title="Browse Template Library"
              >
                <Library className="w-3.5 h-3.5 mr-1" /> Library
              </Button>
            </div>
          </div>
          <Select value={currentTemplate} onValueChange={onTemplateChange}>
            <SelectTrigger className="bg-gray-800/50 border-gray-700 text-white" data-testid="template-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700 max-h-72">
              {allTemplates.map((tpl) => {
                const Icon = templateIcons[tpl.id] || Edit3;
                return (
                  <SelectItem key={tpl.id} value={tpl.id} className="text-white hover:bg-gray-800">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      <span>{tpl.name}</span>
                      {!tpl.builtin && tpl.visibility === 'team' && (
                        <span className="text-[10px] uppercase tracking-wider text-teal-400 ml-1 inline-flex items-center gap-0.5">
                          <Users className="w-3 h-3" /> team
                        </span>
                      )}
                      {!tpl.builtin && tpl.visibility !== 'team' && (
                        <span className="text-[10px] uppercase tracking-wider text-gray-500 ml-1">
                          custom
                        </span>
                      )}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {/* Team-visibility row for editable templates */}
          {canEditTpl && !showSaveAsNew && (
            <div className="flex items-center justify-between mt-3 px-1 py-1">
              <div className="flex items-center gap-2">
                {activeTpl.visibility === 'team' ? (
                  <Users className="w-3.5 h-3.5 text-teal-400" />
                ) : (
                  <Lock className="w-3.5 h-3.5 text-gray-500" />
                )}
                <span className="text-xs text-gray-300">
                  {activeTpl.visibility === 'team' ? 'Shared with team' : 'Private to you'}
                </span>
              </div>
              <Switch
                checked={activeTpl.visibility === 'team'}
                onCheckedChange={handleToggleTplVisibility}
                disabled={savingTpl}
                data-testid="template-visibility-toggle"
              />
            </div>
          )}

          {/* Owner badge for team templates not owned by current user */}
          {activeTpl && !activeTpl.builtin && activeTpl.is_mine === false && (
            <div className="flex items-center gap-2 mt-3 px-2 py-1.5 bg-gray-800/40 border border-gray-700/50 rounded text-[11px] text-gray-400">
              <Users className="w-3 h-3 text-teal-400" />
              <span>Shared by <span className="text-gray-200">{activeTpl.owner_name || 'teammate'}</span> — read-only. Use <span className="text-teal-300">New</span> to fork.</span>
            </div>
          )}

          {/* Template actions */}
          {!showSaveAsNew ? (
            <div className="grid grid-cols-3 gap-1.5 mt-3">
              <Button
                size="sm"
                variant="outline"
                onClick={handleSaveTpl}
                disabled={!canEditTpl || savingTpl}
                className="border-gray-700 bg-gray-800/50 text-gray-200 hover:bg-gray-700 hover:text-white text-xs h-8"
                title={isBuiltinTpl ? 'Built-in templates cannot be modified. Use "New".' : (!canEditTpl ? 'Read-only (owned by teammate)' : 'Save changes')}
                data-testid="save-template-btn"
              >
                <Save className="w-3.5 h-3.5 mr-1" /> Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setShowSaveAsNew(true); setNewTplName(activeTpl?.builtin ? `${activeTpl.name} Copy` : ''); }}
                className="border-teal-800 bg-teal-950/40 text-teal-200 hover:bg-teal-900/50 hover:text-teal-100 text-xs h-8"
                data-testid="save-as-new-template-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> New
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDeleteTpl}
                disabled={!canEditTpl}
                className="border-red-900/60 bg-red-950/30 text-red-200 hover:bg-red-900/40 hover:text-red-100 text-xs h-8 disabled:opacity-40"
                title={isBuiltinTpl ? 'Built-in templates cannot be deleted' : (!canEditTpl ? 'Only the owner can delete' : 'Delete this template')}
                data-testid="delete-template-btn"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
              </Button>
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              <Input
                value={newTplName}
                onChange={(e) => setNewTplName(e.target.value)}
                placeholder="New template name"
                className="bg-gray-800/50 border-gray-700 text-white h-8 text-sm"
                autoFocus
                data-testid="new-template-name-input"
              />
              <div className="grid grid-cols-2 gap-1.5">
                <Button
                  size="sm"
                  onClick={handleSaveAsNew}
                  disabled={!newTplName.trim() || savingTpl}
                  className="bg-teal-500 hover:bg-teal-400 text-gray-900 text-xs h-8"
                  data-testid="confirm-new-template-btn"
                >
                  <Check className="w-3.5 h-3.5 mr-1" /> Create
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setShowSaveAsNew(false); setNewTplName(''); }}
                  className="border-gray-700 bg-gray-800/50 text-gray-300 hover:bg-gray-700 text-xs h-8"
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Cancel
                </Button>
              </div>
            </div>
          )}

          {isBuiltinTpl && !showSaveAsNew && (
            <p className="text-[11px] text-gray-500 mt-2 leading-snug">
              This is a built-in preset. Click <span className="text-teal-300">New</span> to save your edits as a custom template.
            </p>
          )}
        </Card>

        {/* Background Selector */}
        <Card
          className={`p-4 bg-gray-900/50 border-gray-800 relative transition-colors ${
            bgDragOver ? 'border-teal-400 bg-teal-950/30' : ''
          }`}
          data-testid="background-card"
          onDragEnter={handleBgDragEnter}
          onDragOver={handleBgDragOver}
          onDragLeave={handleBgDragLeave}
          onDrop={handleBgDrop}
        >
          {bgDragOver && (
            <div
              className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-teal-950/80 border-2 border-dashed border-teal-400 rounded-lg pointer-events-none"
              data-testid="background-dropzone-overlay"
            >
              <Upload className="w-8 h-8 text-teal-300 mb-2" />
              <div className="text-sm font-medium text-teal-100">Drop images to upload</div>
              <div className="text-[11px] text-teal-300/80 mt-0.5">PNG, JPG, WebP — multi-file OK</div>
            </div>
          )}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-teal-400" />
              <Label className="text-sm font-medium text-gray-300">Background</Label>
            </div>
          </div>
          <Select value={backgroundKey} onValueChange={onBackgroundChange}>
            <SelectTrigger className="bg-gray-800/50 border-gray-700 text-white" data-testid="background-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700 max-h-72">
              {allBackgrounds.map((bg) => (
                <SelectItem key={bg.id} value={bg.id} className="text-white hover:bg-gray-800">
                  <div className="flex items-center gap-2">
                    <FileImage className="w-4 h-4" />
                    <span>{bg.name}</span>
                    {!bg.builtin && bg.visibility === 'team' && (
                      <span className="text-[10px] uppercase tracking-wider text-teal-400 ml-1 inline-flex items-center gap-0.5">
                        <Users className="w-3 h-3" /> team
                      </span>
                    )}
                    {!bg.builtin && bg.visibility !== 'team' && (
                      <span className="text-[10px] uppercase tracking-wider text-gray-500 ml-1">
                        custom
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Team-visibility row for editable backgrounds */}
          {canEditBg && (
            <div className="flex items-center justify-between mt-3 px-1 py-1">
              <div className="flex items-center gap-2">
                {activeBg.visibility === 'team' ? (
                  <Users className="w-3.5 h-3.5 text-teal-400" />
                ) : (
                  <Lock className="w-3.5 h-3.5 text-gray-500" />
                )}
                <span className="text-xs text-gray-300">
                  {activeBg.visibility === 'team' ? 'Shared with team' : 'Private to you'}
                </span>
              </div>
              <Switch
                checked={activeBg.visibility === 'team'}
                onCheckedChange={handleToggleBgVisibility}
                data-testid="background-visibility-toggle"
              />
            </div>
          )}

          {/* Owner badge for team backgrounds not owned by current user */}
          {activeBg && !activeBg.builtin && activeBg.is_mine === false && (
            <div className="flex items-center gap-2 mt-3 px-2 py-1.5 bg-gray-800/40 border border-gray-700/50 rounded text-[11px] text-gray-400">
              <Users className="w-3 h-3 text-teal-400" />
              <span>Shared by <span className="text-gray-200">{activeBg.owner_name || 'teammate'}</span> — read-only.</span>
            </div>
          )}

          {bgFlash && (
            <p className="text-[11px] text-teal-300 mt-2 flex items-center gap-1">
              <Check className="w-3 h-3" /> {bgFlash}
            </p>
          )}

          <div className="grid grid-cols-2 gap-1.5 mt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingBg}
              className="border-teal-800 bg-teal-950/40 text-teal-200 hover:bg-teal-900/50 hover:text-teal-100 text-xs h-8"
              data-testid="upload-background-btn"
            >
              <Upload className="w-3.5 h-3.5 mr-1" />
              {uploadingBg ? 'Uploading…' : 'Upload Image'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDeleteBg}
              disabled={!canEditBg}
              className="border-red-900/60 bg-red-950/30 text-red-200 hover:bg-red-900/40 hover:text-red-100 text-xs h-8 disabled:opacity-40"
              title={activeBg?.builtin ? 'Built-in backgrounds cannot be deleted' : (!canEditBg ? 'Only the owner can delete' : 'Delete this background')}
              data-testid="delete-background-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            multiple
            onChange={handleBgFileChosen}
            className="hidden"
            data-testid="background-file-input"
          />
          {bgError && (
            <p className="text-[11px] text-red-300 mt-2">{bgError}</p>
          )}
          <p className="text-[11px] text-gray-500 mt-2 leading-snug">
            PNG, JPG, or WebP. 16:9 recommended. Max 4 MB each. Drop multiple images anywhere on this card.
          </p>
        </Card>

        <Separator className="bg-gray-800" />

        {/* Music Player */}
        <MusicPlayer />

        <Separator className="bg-gray-800" />

        {/* Title Input */}
        <div className="input-group">
          <Label htmlFor="title" className="text-sm font-medium text-gray-300">Main Title</Label>
          <Textarea
            id="title"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            className="bg-gray-800/50 border-gray-700 text-white min-h-[80px] resize-none"
            placeholder="Enter main title..."
          />
        </div>

        {/* Paragraph Input */}
        <div className="input-group">
          <Label htmlFor="paragraph" className="text-sm font-medium text-gray-300">
            Paragraph Text <span className="text-xs text-gray-500">(2-6 lines recommended)</span>
          </Label>
          <Textarea
            id="paragraph"
            value={paragraph}
            onChange={(e) => onParagraphChange(e.target.value)}
            className="bg-gray-800/50 border-gray-700 text-white min-h-[140px]"
            placeholder={"Enter paragraph text...\nSupports multiple lines."}
          />
        </div>

        {/* Lower Third Input */}
        <div className="input-group">
          <Label htmlFor="lower" className="text-sm font-medium text-gray-300">Lower Third Text</Label>
          <Textarea
            id="lower"
            value={lowerText}
            onChange={(e) => onLowerTextChange(e.target.value)}
            className="bg-gray-800/50 border-gray-700 text-white min-h-[60px] resize-none"
            placeholder="Enter lower third text..."
          />
        </div>

        <Separator className="bg-gray-800" />

        {/* Countdown Timer */}
        <Card className="p-4 bg-gray-900/50 border-gray-800 space-y-3" data-testid="timer-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Timer className="w-4 h-4 text-teal-400" />
              <Label className="text-sm font-medium text-gray-300">Countdown Timer</Label>
            </div>
            <Switch
              checked={timerEnabled}
              onCheckedChange={onTimerToggle}
              data-testid="timer-toggle"
            />
          </div>

          {timerEnabled && (
            <div className="space-y-3 pt-1">
              <div className="flex items-center justify-between py-1">
                <div>
                  <Label className="text-sm font-medium text-gray-300">Manual Calculation</Label>
                  <p className="text-xs text-gray-500 mt-0.5">Enter current time instead of using clock</p>
                </div>
                <Switch
                  checked={manualMode}
                  onCheckedChange={onManualModeToggle}
                  data-testid="manual-mode-toggle"
                />
              </div>

              <div className="flex items-center justify-between py-1">
                <div>
                  <Label className="text-sm font-medium text-gray-300">Auto-close presenter at 0:00</Label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Closes the presenter window so Zoom / Teams returns to normal view
                  </p>
                </div>
                <Switch
                  checked={!!autoCloseOnTimerEnd}
                  onCheckedChange={onAutoCloseToggle}
                  data-testid="auto-close-toggle"
                />
              </div>

              {manualMode && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-400">Current Time</Label>
                  <TimePicker12h value={manualCurrentTime} onChange={onManualCurrentTimeChange} />
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-xs text-gray-400">Resume At</Label>
                <TimePicker12h value={targetTime} onChange={onTargetTimeChange} />
              </div>
              {!manualMode && (
                <TimezoneSelect
                  value={targetTimezone || 'local'}
                  onChange={onTargetTimezoneChange}
                />
              )}
              <div className="space-y-1.5">
                <Label htmlFor="timer-label" className="text-xs text-gray-400">Timer Label</Label>
                <Input
                  id="timer-label"
                  value={timerLabel}
                  onChange={(e) => onTimerLabelChange(e.target.value)}
                  className="bg-gray-800/50 border-gray-700 text-white"
                  placeholder="Session resumes in"
                  data-testid="timer-label-input"
                />
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                {manualMode
                  ? 'Duration is calculated from your custom current time. Useful for previewing or overriding the system clock.'
                  : 'Counts down to the selected time today. If the time has passed, it rolls to tomorrow automatically.'}
              </p>
            </div>
          )}
        </Card>

        <Separator className="bg-gray-800" />

        <Card className="p-4 bg-gray-900/50 border-gray-800">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium text-gray-300">Ambient Animation</Label>
              <p className="text-xs text-gray-500 mt-1">Particles, light sweeps, parallax</p>
            </div>
            <Switch
              checked={animationEnabled}
              onCheckedChange={onAnimationToggle}
            />
          </div>
        </Card>

        {/* Zoom Tips */}
        <Card className="p-4 bg-teal-950/30 border-teal-900/50">
          <h3 className="text-sm font-semibold text-teal-300 mb-2">Zoom / Teams Sharing</h3>
          <ul className="text-xs text-teal-200/70 space-y-1">
            <li>1. Click <span className="text-teal-200">Presenter</span> to open the live display</li>
            <li>2. In Zoom/Teams, choose <span className="text-teal-200">Share Window</span></li>
            <li>3. Select <span className="text-teal-200">IRI Presenter</span> window</li>
            <li>4. Edit titles, timer, templates here — updates sync live</li>
            <li>5. Your templates and backgrounds are saved to your account</li>
          </ul>
        </Card>
      </div>

      <TemplateLibrary
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        currentTemplate={currentTemplate}
        onPick={onTemplateChange}
      />
    </div>
  );
};

export default ControlPanel;
